import { createContext, useContext } from 'react';
import type { GalleryAsset, Rating } from '@xphoto/shared';

/**
 * Pont entre la GALERIE et la ROUTE visionneuse (`/photo/:id`).
 *
 * La grille virtualisée charge les assets par INDEX (tableau creux) ; la route
 * visionneuse, elle, connaît un ID. Ce contexte — fourni par `GalleryView` quand
 * la grille est montée — expose de quoi :
 *  - retrouver l'index d'un id chargé (`indexOfId`) → compteur + préc./suiv. ;
 *  - lire un asset par index (`getAsset`) → id du voisin pour l'URL suivante ;
 *  - garantir le chargement d'une plage (`ensureRange`) → préchargement des voisins ;
 *  - notifier un changement de note (`onRatingChange`) → tri « mieux notées ».
 *
 * Absent (contexte === null) ⇒ accès À FROID : la route visionneuse charge l'asset
 * seul par son id, sans préc./suiv. (dégradation propre, cf. mission routing).
 */
export interface GalleryViewerContextValue {
  total: number;
  /** Asset à l'index absolu, ou `undefined` si sa page n'est pas encore chargée. */
  getAsset: (index: number) => GalleryAsset | undefined;
  /** Index absolu d'un id parmi les assets chargés, ou `-1` si introuvable/absent. */
  indexOfId: (id: string) => number;
  /** Garantit le chargement de la plage `[start, end)` (préchargement des voisins). */
  ensureRange: (start: number, end: number) => void;
  /** Répercute un changement de note d'un asset vers la grille. */
  onRatingChange: (assetId: string, rating: Rating) => void;
}

export const GalleryViewerContext = createContext<GalleryViewerContextValue | null>(null);

/** Contexte galerie de la visionneuse (null en accès à froid). */
export function useGalleryViewerContext(): GalleryViewerContextValue | null {
  return useContext(GalleryViewerContext);
}
